/**
 * Force-refresh the Clerk session token.
 *
 * Clerk's session JWT is short-lived (~60s). The Clerk SDK auto-refreshes on a
 * timer, but a suspended/backgrounded tab won't fire its interval — the token
 * silently expires and the next server call fails with "Authentication required"
 * or 403. Calling this before retrying reissues the cookie so the retry succeeds.
 *
 * No-ops on the server (Clerk's session lives on the client). Concurrent
 * callers share a single in-flight refresh so a burst of failed queries doesn't
 * fan out to N token requests. Best-effort: refresh errors are swallowed so the
 * caller surfaces the underlying failure.
 */
let inFlight: Promise<void> | null = null;

export async function refreshClerkSessionToken(): Promise<void> {
  if (typeof window === "undefined") return;
  if (inFlight) return inFlight;

  const refresh = getClerkTokenRefresher();
  if (!refresh) return;

  inFlight = Promise.resolve(refresh())
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Narrow `window.Clerk` to just the `session.getToken` call we depend on, via
 * runtime checks rather than a type assertion. We don't consume the returned
 * token — calling `getToken` with `skipCache` is what triggers Clerk to reissue
 * the session cookie.
 */
function getClerkTokenRefresher(): (() => unknown) | null {
  if (typeof window === "undefined") return null;
  const clerk: unknown = window.Clerk;
  if (!clerk || typeof clerk !== "object" || !("session" in clerk)) return null;
  const session = clerk.session;
  if (!session || typeof session !== "object") return null;
  if (!("getToken" in session) || typeof session.getToken !== "function") {
    return null;
  }
  const getToken = session.getToken.bind(session);
  return () => getToken({ skipCache: true });
}
